/**
 * Plano 9 / A9-05 — System Health hub.
 * Página única que consolida links e status resumido de:
 *   - /health (sistema geral)
 *   - /models-health (providers/IA)
 *   - /memory-health (memória + decay)
 *   - /admin/providers/health (rotas de modelos + Anti-Gemini gate)
 * Reduz fragmentação sem remover páginas legadas (compat).
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { Activity, Brain, Cpu, ShieldCheck, ArrowRight, Gauge, ShieldAlert, RotateCcw, FlaskConical } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { ProviderVerificationCard } from "@/components/admin/ProviderVerificationCard";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

const PANELS = [
  {
    to: "/health",
    icon: Activity,
    title: "Sistema",
    description: "Webhook, edge functions, banco e cron.",
    badge: "geral",
  },
  {
    to: "/models-health",
    icon: Cpu,
    title: "Modelos & IA",
    description: "Providers, latência, custo, breakers.",
    badge: "providers",
  },
  {
    to: "/memory-health",
    icon: Brain,
    title: "Memória",
    description: "5 layers, decay, quarantine, promoção.",
    badge: "memory",
  },
  {
    to: "/admin/providers/health",
    icon: ShieldCheck,
    title: "Anti-Gemini Gate",
    description: "Status do A9-01: providers BYOK + lockdown.",
    badge: "lockdown",
  },
] as const;

export default function SystemHealth() {
  const { data: orgId } = useCurrentOrgId();
  const { data: operational } = useQuery({
    queryKey: ["system-health-operational-summary", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const [scorecardRes, alertsRes, baselinesRes, rolloutRes, rollbackRes] = await Promise.all([
        supabase.from("sentinel_scorecards").select("overall_score, created_at").eq("org_id", orgId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("sentinel_alerts").select("id, status", { count: "exact" }).eq("org_id", orgId).in("status", ["open", "acknowledged"]),
        supabase.from("sentinel_baselines").select("id", { count: "exact" }).eq("org_id", orgId).is("valid_until", null),
        supabase.from("cortex_feature_flags").select("flag_key, enabled").eq("enabled", true),
        supabase.from("rollback_events").select("id", { count: "exact" }).eq("org_id", orgId),
      ]);

      if (scorecardRes.error) throw scorecardRes.error;
      if (alertsRes.error) throw alertsRes.error;
      if (baselinesRes.error) throw baselinesRes.error;
      if (rolloutRes.error) throw rolloutRes.error;
      if (rollbackRes.error) throw rollbackRes.error;

      return {
        latestScore: scorecardRes.data?.overall_score ?? null,
        latestScoreAt: scorecardRes.data?.created_at ?? null,
        openAlerts: alertsRes.count ?? 0,
        activeBaselines: baselinesRes.count ?? 0,
        liveFlags: rolloutRes.data?.length ?? 0,
        rollbackCount: rollbackRes.count ?? 0,
      };
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "System Health" }]} />
      <div>
        <h1 className="text-2xl font-bold font-headline">System Health</h1>
        <p className="text-sm text-muted-foreground">
          Hub central de saúde do sistema. Consolida visões antes fragmentadas em páginas separadas.
        </p>
        {!orgId ? <p className="text-sm text-muted-foreground mt-2">Nenhuma organização ativa encontrada para carregar os indicadores.</p> : null}
      </div>

      <ShadowModeBanner />

      <div className="grid gap-4 md:grid-cols-2">
        {PANELS.map((p) => {
          const Icon = p.icon;
          return (
            <Card key={p.to} className="hover:border-primary/50 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-primary" />
                    <CardTitle className="text-base">{p.title}</CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs">{p.badge}</Badge>
                </div>
                <CardDescription className="text-xs">{p.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to={p.to}>
                  <Button variant="outline" size="sm" className="w-full justify-between">
                    Abrir painel
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SnapshotCard
          title="Readiness score"
          value={operational?.latestScore != null ? Number(operational.latestScore).toFixed(2) : "—"}
          hint={operational?.latestScoreAt ? `Último scorecard: ${new Date(operational.latestScoreAt).toLocaleString("pt-BR")}` : "Aguardando scorecard"}
          icon={Gauge}
        />
        <SnapshotCard
          title="Alertas abertos"
          value={String(operational?.openAlerts ?? 0)}
          hint="Fila operacional ativa"
          icon={ShieldAlert}
        />
        <SnapshotCard
          title="Baselines ativos"
          value={String(operational?.activeBaselines ?? 0)}
          hint="Comparação operacional disponível"
          icon={FlaskConical}
        />
        <SnapshotCard
          title="Rollbacks"
          value={String(operational?.rollbackCount ?? 0)}
          hint={`${operational?.liveFlags ?? 0} flags em rollout`}
          icon={RotateCcw}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <QuickLinkCard title="Alerts & SLOs" description="Burn rate, alertas, SLO catalog e evaluator." to="/admin/alerts-slos" />
        <QuickLinkCard title="Flags & rollout" description="Gates, calendário, smoke tests e rollback por flag." to="/sistema/flags" />
        
      </div>

      <ProviderVerificationCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Como usar</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• <strong>Sistema:</strong> verificar fluxo do webhook, errors, cron jobs.</p>
          <p>• <strong>Modelos & IA:</strong> latência por provider, breaker state, fallback activations.</p>
          <p>• <strong>Memória:</strong> distribuição por layer, decay rate, promotion queue.</p>
          <p>• <strong>Anti-Gemini Gate:</strong> garantir que Gemini só entra como fallback.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function SnapshotCard({ title, value, hint, icon: Icon }: { title: string; value: string; hint: string; icon: typeof Gauge }) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="w-4 h-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

function QuickLinkCard({ title, description, to }: { title: string; description: string; to: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Link to={to}>
          <Button variant="outline" size="sm" className="w-full justify-between">
            Abrir painel
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
